import { flow } from 'lodash';

import UploadSalesCtrl from '../../shifts/UploadSalesCtrl';
import UploadSalesUrl from '../../shifts/modal-sales-upload.html';
import { SALES_TYPES } from '../../constants';

class SalesService {
  constructor(
    Authentication,
    PersonaCollection,
    Restangular,
    Utils,
    UserUtils,
    sdk,
    roundFilter,
    displayNumberFilter,
    $uibModal
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      PersonaCollection,
      Restangular,
      Utils,
      UserUtils,
      sdk,
      roundFilter,
      displayNumberFilter,
      $uibModal,
    });
    Object.assign(this, {
      getEmptyProjectedSale: sdk('process/getEmptyProjectedSale'),
      getProjectedSales: sdk('process/getProjectedSales'),
    });

    this.clearData();
  }

  clearData() {
    this.defaultsChanged = false;
    this.defaultSales = {};
    this.defaultSalesTotals = {};
    this.overrideSales = [];
    this.cachedDays = {};
  }

  async saveDefaultSales(sales) {
    if (!sales.length) {
      return;
    }

    const salesToBulkUpdate = [];
    sales.forEach((sale) => {
      if (!sale.id && !sale.amount) {
        return;
      }

      if (!sale.amount) {
        sale.amount = 0;
        sale.deleted = true;
      }

      if (!sale.locationId) {
        sale.locationId = sale.location.id;
        delete sale.location;
      }

      salesToBulkUpdate.push(sale);
    });

    if (salesToBulkUpdate.length) {
      const response = await this.Restangular.all('/sales/list/bulk').customPOST(salesToBulkUpdate);
      this.defaultsChanged = true;
      return this._syncSales(sales, response);
    }
  }

  async importSales(payload) {
    const imported = await this.Restangular.all('/sales/list/import').customPOST(payload);
    flow([this.groupSalesByLocation.bind(this), Object.values, this._addSalesToCache.bind(this)])(
      imported
    );
  }

  groupSalesByLocation(sales) {
    return sales.reduce((acc, sale) => {
      if (!(sale.locationId in acc)) {
        acc[sale.locationId] = {
          location: {
            id: sale.locationId,
          },
          sales: [],
        };
      }

      acc[sale.locationId].sales.push(sale);
      return acc;
    }, {});
  }

  _syncSales(sales, resp) {
    this.defaultSales[sales[0].locationId] = sales.map((sale) => {
      if (sale.deleted) {
        return this.getEmptyProjectedSale(SALES_TYPES.default, sale.locationId, sale.weekday);
      }

      return resp.find((item) => sale.id === item.id || sale.weekday === item.weekday) || sale;
    });

    this.defaultSalesTotals[sales[0].locationId] = this.sumSales(
      this.defaultSales[sales[0].locationId]
    );

    return this.defaultSales[sales[0].locationId];
  }

  async saveSingleSale(sale) {
    if (!sale.id && !sale.amount) {
      return sale;
    }

    sale.locationId = sale.locationId || sale.location.id;
    let newSale;
    if (sale.id) {
      this.Restangular.restangularizeElement(null, sale, 'sales');
      if (sale.amount) {
        newSale = await sale.put();
      } else {
        newSale = await this.deleteSale(sale);
      }
    } else {
      newSale = await this.Restangular.all('sales/list').post(sale);
    }

    if (sale.type !== SALES_TYPES.actual) {
      this.putInCache(newSale);
    }

    return newSale;
  }

  putInCache(sale) {
    const { salesForLocation, saleIndex } = this.findSaleInCache(sale);

    if (!salesForLocation) {
      this.overrideSales.push({
        location: {
          id: sale.locationId || sale.location.id,
        },
        sales: [sale],
      });
      return;
    }
    if (saleIndex < 0) {
      salesForLocation.push(sale);
      return;
    }

    salesForLocation[saleIndex] = sale;
  }

  findSaleInCache(sale) {
    let saleIndex;
    const newSaleLocationId = sale.locationId || sale.location.id;
    let salesForLocation = this.overrideSales.find(
      (overrides) => newSaleLocationId === overrides.location.id
    );
    if (salesForLocation) {
      salesForLocation = salesForLocation.sales;
      saleIndex = salesForLocation.findIndex(
        (override) => override.id === sale.id || override.date === sale.date
      );
    }

    return {
      salesForLocation,
      saleIndex,
    };
  }

  async deleteSale(sale) {
    const locationId = sale.locationId;
    if (sale.type === SALES_TYPES.default) {
      const dayNo = sale.weekday;
      await sale.remove();

      return this.getEmptyProjectedSale(SALES_TYPES.default, locationId, dayNo);
    }

    const initialDate = sale.date;
    const dates = `${sale.date}/${sale.date}`;
    await sale.remove();
    const salesList = await this.Restangular.one('sales/list', locationId).get({ dates });
    const emptySale = this.getEmptyProjectedSale(sale.type, locationId, initialDate);

    return salesList.length ? { ...emptySale, default: salesList[0].amount } : emptySale;
  }

  async getDefaultSales(locationId) {
    if (!locationId) {
      return [];
    }

    await this.fetchAllDefaultSales();
    return this.defaultSales[locationId];
  }

  async fetchAllDefaultSales() {
    if (this.defaultSales && Object.keys(this.defaultSales).length) {
      return this.defaultSales;
    }

    const sales = await this.Restangular.one('sales/list').get('sales');
    this.setDefaultSales(sales);

    return this.defaultSales;
  }

  setDefaultSales(sales = []) {
    this.defaultSales = {};
    this.defaultSalesTotals = {};
    sales.forEach((salesGroup) => {
      this.defaultSales[salesGroup.location.id] = salesGroup.sales;
      this.defaultSalesTotals[salesGroup.location.id] = this.sumSales(salesGroup.sales);
    });
  }

  async getSales(days) {
    if (days.length < 2) {
      return this.Restangular.one('sales/list').get({ dates: days[0] });
    }

    const sortedDates = [...days].sort(sortByDate.bind(this));
    const dates = `${sortedDates[0]}/${sortedDates[sortedDates.length - 1]}`;
    return await this.Restangular.one('sales/list').get({ dates });

    function sortByDate(a, b) {
      return this.Utils.getRealDate(a) - this.Utils.getRealDate(b);
    }
  }

  sumSales(sales = []) {
    const amounts = sales.map((sale) => Number(sale.amount) || 0);
    return this.roundFilter(this.Utils.sum(amounts), 2);
  }

  async getProcessedProjectedSales(currentDays, locations, filteredLocationIds, onlyCache = false) {
    if (!this.Authentication.user.isManager || !this.Authentication.user.hasAccessToLaborCost) {
      return;
    }

    const locationIds = locations.map((loc) => loc.id);
    let loadedSales;
    if (
      !this.defaultsChanged &&
      (onlyCache || this._checkIfOverridesCached(currentDays, locationIds))
    ) {
      loadedSales = this.overrideSales;
    } else {
      loadedSales = await this.getSales(currentDays);
      this._addSalesToCache(loadedSales);
      this._addDaysToCache(currentDays, locationIds);
    }

    return this._getProjectedSales(loadedSales, filteredLocationIds, currentDays);
  }

  _getProjectedSales(sales, locIds, currentDays) {
    return this.getProjectedSales(sales, locIds, currentDays);
  }

  _checkIfOverridesCached(days, locationIds) {
    return locationIds.every(
      (id) => id in this.cachedDays && days.every((day) => this.cachedDays[id].includes(day))
    );
  }

  canEditSales(group, allLocations = [], filters = {}) {
    let location = group;
    if (allLocations.length === 1) {
      location = allLocations[0];
    }

    if (filters.locationIds?.length === 1) {
      location = this.PersonaCollection.personasDict[filters.locationIds[0]];
    }

    return this.Authentication.user.hasAccessToLaborCost && this.canEditGroup(location);
  }

  canEditGroup(group) {
    const key = group.key || group.id;
    return (
      key in this.PersonaCollection.personasDict &&
      this.UserUtils.canEditGroup(this.PersonaCollection.personasDict[key])
    );
  }

  _addSalesToCache(sales = {}) {
    sales.forEach((projectedSale) => {
      const locationId = projectedSale.location.id || projectedSale.locationId;
      const cachedIndex = this.overrideSales.findIndex(
        (cached) => cached.location.id === locationId
      );
      if (cachedIndex < 0) {
        this.overrideSales.push(projectedSale);
        return;
      }

      projectedSale.sales.reduce((reduced, sale) => {
        const index = reduced.findIndex((s) => s.date === sale.date);
        if (index < 0) {
          reduced.push(sale);
        } else {
          reduced[index] = sale;
        }

        return reduced;
      }, this.overrideSales[cachedIndex].sales);
    });
  }

  _addDaysToCache(days = [], locationsIds = []) {
    locationsIds.forEach((id) => {
      if (!(id in this.cachedDays)) {
        this.cachedDays[id] = days;
        return;
      }

      days.reduce((cached, day) => {
        if (!cached.includes(day)) {
          cached.push(day);
        }

        return cached;
      }, this.cachedDays[id]);
    });
  }

  uploadSales(type = SALES_TYPES.override) {
    return this.$uibModal.open({
      templateUrl: UploadSalesUrl,
      size: 'lg',
      windowClass: 'upload-sales',
      controllerAs: 'ctrl',
      controller: UploadSalesCtrl,
      resolve: {
        type() {
          return type;
        },
      },
    }).result;
  }
}

export default SalesService;
